import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Shield } from "lucide-react";
import SuperadminOverview from "@/components/superadmin/SuperadminOverview";
import SuperadminWorkspaces from "@/components/superadmin/SuperadminWorkspaces";
import SuperadminPlans from "@/components/superadmin/SuperadminPlans";
import SuperadminAudit from "@/components/superadmin/SuperadminAudit";
import SuperadminUsers from "@/components/superadmin/SuperadminUsers";

export default function SuperadminPage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");

  // Auth state is still loading
  if (user === null) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-[#FAF9F7]">
        <p className="font-sans text-sm text-[#7A7571]">Loading…</p>
      </div>
    );
  }
  // Logged out
  if (!user || !user.id) return <Navigate to="/login" replace />;
  // Logged in but not a superadmin
  if (!user.is_superadmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div
      className="min-h-screen w-full bg-[#FAF9F7] text-[#2C2A29]"
      data-testid="superadmin-page"
    >
      <header className="border-b border-[#DCD7CE] bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="superadmin-back-link"
              className="inline-flex items-center gap-1.5 px-2 py-1 -ml-2 rounded-md font-sans text-sm text-[#7A7571] hover:bg-[#EFEAE0] hover:text-[#2C2A29] transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={2} />
              Back to app
            </Link>
            <span className="h-5 w-px bg-[#DCD7CE]" />
            <div className="flex items-center gap-2">
              <Shield size={16} className="text-[#D96C4A]" strokeWidth={2} />
              <span className="font-serif text-xl tracking-tight">
                Superadmin
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="font-sans text-sm text-[#2C2A29] font-semibold">
                {user.display_name}
              </div>
              <div className="font-sans text-xs text-[#7A7571]">
                {user.email}
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="superadmin-logout-button"
              className="px-3 py-2 rounded-md font-sans text-sm text-[#8C3A1F] hover:bg-[#8C3A1F]/10 transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 py-8">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList
            className="bg-transparent border-b border-[#DCD7CE] rounded-none w-full justify-start gap-1 p-0 h-auto mb-8"
            data-testid="superadmin-tabs"
          >
            <TabTrigger value="overview" label="Overview" />
            <TabTrigger value="workspaces" label="Workspaces" />
            <TabTrigger value="plans" label="Plans" />
            <TabTrigger value="audit" label="Audit log" />
            <TabTrigger value="users" label="Users" />
          </TabsList>

          <TabsContent value="overview" className="mt-0">
            <SuperadminOverview />
          </TabsContent>
          <TabsContent value="workspaces" className="mt-0">
            <SuperadminWorkspaces />
          </TabsContent>
          <TabsContent value="plans" className="mt-0">
            <SuperadminPlans />
          </TabsContent>
          <TabsContent value="audit" className="mt-0">
            <SuperadminAudit />
          </TabsContent>
          <TabsContent value="users" className="mt-0">
            <SuperadminUsers />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function TabTrigger({ value, label }) {
  return (
    <TabsTrigger
      value={value}
      data-testid={`superadmin-tab-${value}`}
      className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#D96C4A] data-[state=active]:bg-transparent data-[state=active]:text-[#2C2A29] data-[state=active]:shadow-none text-[#7A7571] font-sans text-sm font-medium px-4 py-3"
    >
      {label}
    </TabsTrigger>
  );
}
